import type { SxProps, Theme } from "@mui/material";
import {
  hierarchyTableColors,
  hierarchyTableLayout,
  hierarchyTableZIndex,
  levelTypography,
  pxToRem,
} from "../Constants/constants";
import type {
  BilledHoursVariant,
  HierarchyLevel,
  HierarchyTableDataCellOptions,
  HierarchyTableHeaderCellOptions,
  HierarchyTableTotalDataCellOptions,
} from "../Types/types";

const getDataCellTypographyStyles = (level: HierarchyLevel) => {
  const { fontSize, fontWeight } = levelTypography[level];
  return {
    fontSize,
    fontWeight,
    color:
      level === "organization"
        ? hierarchyTableColors.textPrimary
        : hierarchyTableColors.textDataChild,
    lineHeight: 1.26,
  };
};

const getBilledHoursTypographyStyles = (level: HierarchyLevel) => {
  const { fontSize, fontWeight } = levelTypography[level];
  return {
    fontSize,
    fontWeight,
    color: hierarchyTableColors.textPrimary,
    lineHeight: 1.26,
  };
};

const getHierarchyIndentRem = (depth: number): string =>
  pxToRem(
    hierarchyTableLayout.hierarchyIndentBase +
      depth * hierarchyTableLayout.hierarchyIndentStep,
  );

export const hierarchyTableStyles = {
  table: {
    borderCollapse: "separate",
    borderSpacing: 0,
    minWidth: hierarchyTableLayout.tableMinWidth,
    "& thead tr:first-of-type .MuiTableCell-root": {
      position: "sticky",
      top: 0,
      zIndex: hierarchyTableZIndex.headerCell,
    },
    "& thead tr:first-of-type .MuiTableCell-root:first-of-type": {
      left: 0,
      zIndex: hierarchyTableZIndex.headerStickyCorner,
    },
    "& thead tr:nth-of-type(2) .MuiTableCell-root": {
      position: "sticky",
      top: hierarchyTableLayout.headerFirstRowHeight,
      zIndex: hierarchyTableZIndex.headerCell,
    },
  } as SxProps<Theme>,

  headerCell: (
    options?: HierarchyTableHeaderCellOptions,
  ): SxProps<Theme> => ({
    backgroundColor: options?.bg ?? hierarchyTableColors.headerBg,
    color: hierarchyTableColors.textPrimary,
    fontWeight: 500,
    fontSize: pxToRem(16),
    textAlign: options?.align ?? "center",
    verticalAlign: "middle",
    borderRight: options?.noBorderRight
      ? "none"
      : `${options?.borderWidth ?? hierarchyTableLayout.borderThin} solid ${hierarchyTableColors.border}`,
    borderTop: options?.noBorderTop
      ? "none"
      : `${hierarchyTableLayout.borderThin} solid ${hierarchyTableColors.border}`,
    borderLeft: options?.noBorderLeft
      ? "none"
      : `${hierarchyTableLayout.borderThin} solid ${hierarchyTableColors.border}`,
    borderBottom: options?.noBorderBottom
      ? "none"
      : `${hierarchyTableLayout.borderThin} solid ${hierarchyTableColors.border}`,
    minWidth: options?.width,
    width: options?.width,
    px: 1.5,
    py: 1,
    boxSizing: "border-box",
    ...(options?.sticky
      ? {
          position: "sticky",
          left: 0,
          top: 0,
          zIndex: hierarchyTableZIndex.headerStickyCorner,
        }
      : {}),
    ...(options?.borderRadius ? { borderRadius: options.borderRadius } : {}),
    ...(options?.minHeight ? { minHeight: options.minHeight } : {}),
    ...(options?.height ? { height: options.height } : {}),
  }),

  headerTitle: {
    fontSize: pxToRem(16),
    fontWeight: 500,
  } as SxProps<Theme>,

  headerSubtext: {
    display: "block",
    fontSize: pxToRem(13),
    fontWeight: 400,
    color: hierarchyTableColors.textPrimary,
    lineHeight: 1.3,
    mt: 0.25,
  } as SxProps<Theme>,

  billedHoursHeaderTitle: {
    fontWeight: 600,
    fontSize: pxToRem(18),
  } as SxProps<Theme>,

  billedHoursSubtext: {
    display: "block",
    fontSize: pxToRem(18),
    fontWeight: 400,
    color: hierarchyTableColors.textPrimary,
    lineHeight: 1.3,
    mt: 0.25,
  } as SxProps<Theme>,

  billedHoursSubHeader: (
    variant: BilledHoursVariant,
  ): SxProps<Theme> => {
    const bgMap = {
      offshore: hierarchyTableColors.offshoreBg,
      onsite: hierarchyTableColors.onsiteBg,
      total: hierarchyTableColors.totalBg,
    };
    return {
      backgroundColor: bgMap[variant],
      color: hierarchyTableColors.textPrimary,
      fontWeight: 500,
      fontSize: pxToRem(16),
      textAlign: "center",
      borderRight: "none",
      borderTop: "none",
      borderBottom: `${hierarchyTableLayout.borderThin} solid ${hierarchyTableColors.borderBilledHours}`,
      borderLeft: "none",
      minWidth: hierarchyTableLayout.billedHoursSubColWidth,
      width: hierarchyTableLayout.billedHoursSubColWidth,
      height: hierarchyTableLayout.headerSecondRowHeight,
      boxSizing: "border-box",
      py: 0.5,
      position: "sticky",
      top: hierarchyTableLayout.headerFirstRowHeight,
      zIndex: hierarchyTableZIndex.headerCell,
    };
  },

  dataCell: (
    level: HierarchyLevel,
    options?: HierarchyTableDataCellOptions,
  ): SxProps<Theme> => ({
    ...getDataCellTypographyStyles(level),
    backgroundColor:
      options?.bg ??
      (level === "organization"
        ? hierarchyTableColors.hierarchyCellBg
        : hierarchyTableColors.childHierarchyCellBg),
    textAlign: options?.align ?? "right",
    borderRight: options?.noBorderRight
      ? "none"
      : `${hierarchyTableLayout.borderThin} solid ${hierarchyTableColors.border}`,
    borderBottom: options?.noBorderBottom
      ? "none"
      : `${hierarchyTableLayout.borderDefault} solid ${hierarchyTableColors.borderBottom}`,
    borderLeft: options?.noBorderLeft
      ? "none"
      : `${hierarchyTableLayout.borderThin} solid ${hierarchyTableColors.border}`,
    borderTop: options?.noBorderTop
      ? "none"
      : `${hierarchyTableLayout.borderThin} solid ${hierarchyTableColors.border}`,
    minWidth: options?.width,
    width: options?.width,
    px: 1.5,
    py: 1.5,
    whiteSpace: "nowrap",
    ...(options?.isBilledHours
      ? { backgroundColor: hierarchyTableColors.billedHoursRowTint }
      : {}),
  }),

  hierarchyLabelText: (level: HierarchyLevel): SxProps<Theme> => {
    const typography = levelTypography[level];
    return {
      overflow: "hidden",
      textOverflow: "ellipsis",
      fontSize: typography.fontSize,
      fontWeight: typography.fontWeight,
      color: typography.color,
      lineHeight: 1.26,
    };
  },

  hierarchyCell: (depth: number, expandable = false): SxProps<Theme> => ({
    position: "sticky",
    left: 0,
    zIndex: hierarchyTableZIndex.bodyStickyCell,
    backgroundColor:
      depth === 0
        ? hierarchyTableColors.hierarchyCellBg
        : hierarchyTableColors.childHierarchyCellBg,
    borderRight: `${hierarchyTableLayout.borderDefault} solid ${hierarchyTableColors.border}`,
    borderBottom: `${hierarchyTableLayout.borderDefault} solid ${hierarchyTableColors.borderBottom}`,
    minWidth: hierarchyTableLayout.hierarchyColWidth,
    width: hierarchyTableLayout.hierarchyColWidth,
    maxWidth: hierarchyTableLayout.hierarchyColWidth,
    pl: getHierarchyIndentRem(depth),
    pr: 1.5,
    py: 1.5,
    ...(expandable ? { cursor: "pointer" } : {}),
  }),

  expandIcon: {
    width: hierarchyTableLayout.expandIconSize,
    height: hierarchyTableLayout.expandIconSize,
    flexShrink: 0,
  } as SxProps<Theme>,

  expandIconPlaceholder: {
    width: hierarchyTableLayout.expandIconPlaceholderWidth,
    mr: 0.5,
    flexShrink: 0,
  } as SxProps<Theme>,

  hierarchyLabelRow: {
    display: "flex",
    alignItems: "center",
    gap: 0.5,
    minHeight: hierarchyTableLayout.hierarchyLabelMinHeight,
  } as SxProps<Theme>,

  billedHoursMetricCell: (
    level: HierarchyLevel,
    variant: BilledHoursVariant,
  ): SxProps<Theme> => {
    const bgMap = {
      offshore: hierarchyTableColors.offshoreBg,
      onsite: hierarchyTableColors.onsiteBg,
      total: hierarchyTableColors.totalBg,
    };
    return {
      ...getBilledHoursTypographyStyles(level),
      backgroundColor: bgMap[variant],
      textAlign: "center",
      borderRight: `${hierarchyTableLayout.borderThin} solid ${hierarchyTableColors.border}`,
      borderBottom: `${hierarchyTableLayout.borderDefault} solid ${hierarchyTableColors.borderBilledHours}`,
      minWidth: hierarchyTableLayout.billedHoursSubColWidth,
      width: hierarchyTableLayout.billedHoursSubColWidth,
      py: 1.5,
    };
  },

  totalHierarchyCell: {
    position: "sticky",
    left: 0,
    zIndex: hierarchyTableZIndex.totalStickyCell,
    backgroundColor: hierarchyTableColors.hierarchyCellBg,
    borderRight: `${hierarchyTableLayout.borderDefault} solid ${hierarchyTableColors.border}`,
    minWidth: hierarchyTableLayout.hierarchyColWidth,
    width: hierarchyTableLayout.hierarchyColWidth,
    maxWidth: hierarchyTableLayout.hierarchyColWidth,
    pl: pxToRem(12),
    pr: 1.5,
    py: 1.5,
    fontSize: pxToRem(16),
    fontWeight: 500,
    color: hierarchyTableColors.textPrimary,
    lineHeight: 1.26,
    borderRadius: "0 0 0 0",
  } as SxProps<Theme>,

  totalDataCell: (
    options?: HierarchyTableTotalDataCellOptions,
  ): SxProps<Theme> => ({
    backgroundColor: hierarchyTableColors.childHierarchyCellBg,
    color: hierarchyTableColors.textDataChild,
    fontWeight: 500,
    fontSize: pxToRem(16),
    lineHeight: 1.26,
    textAlign: options?.align ?? "right",
    borderRight: options?.noBorderRight
      ? "none"
      : `${hierarchyTableLayout.borderThin} solid ${hierarchyTableColors.border}`,
    minWidth: options?.width,
    width: options?.width,
    px: 1.5,
    py: 1.5,
    whiteSpace: "nowrap",
  }),

  totalBilledHoursMetricCell: (
    variant: BilledHoursVariant,
  ): SxProps<Theme> => {
    const bgMap = {
      offshore: hierarchyTableColors.offshoreBg,
      onsite: hierarchyTableColors.onsiteBg,
      total: hierarchyTableColors.totalBg,
    };
    return {
      backgroundColor: bgMap[variant],
      color: hierarchyTableColors.textPrimary,
      fontWeight: 500,
      fontSize: pxToRem(16),
      lineHeight: 1.26,
      textAlign: "center",
      borderRight: `${hierarchyTableLayout.borderThin} solid ${hierarchyTableColors.border}`,
      minWidth: hierarchyTableLayout.billedHoursSubColWidth,
      width: hierarchyTableLayout.billedHoursSubColWidth,
      py: 1.5,
    };
  },

  infoSection: {
    backgroundColor: hierarchyTableColors.infoSectionBg,
    borderRadius: hierarchyTableLayout.borderRadiusSm,
    px: 0.5,
    py: 0.25,
    mt: 1,
    flexShrink: 0,
  } as SxProps<Theme>,

  infoSectionText: {
    fontSize: pxToRem(13),
    fontWeight: 400,
    lineHeight: 1.5,
    color: hierarchyTableColors.textPrimary,
  } as SxProps<Theme>,

  tableContainerStack: {
    flex: 1,
    minHeight: 0,
    display: "flex",
    flexDirection: "column",
  } as SxProps<Theme>,
};
